// Shared lifecycle types and helpers used by both session and conversation lifecycles.
// useNamingConvention is disabled for this file via biome.mjs override.

import type { BorgStatus } from "../models/borg-types";

// -- Polling phase --

export type PollingPhase = "idle" | "loading" | "in_progress" | "complete" | "error";

export const derivePollingPhase = (data: { status: BorgStatus } | undefined, isLoading: boolean): PollingPhase => {
	if (data == null) {
		return isLoading ? "loading" : "idle";
	}
	switch (data.status) {
		case "completed":
			return "complete";
		case "failed":
		case "timed_out":
		case "canceling":
		case "cancelled":
			return "error";
		default:
			return "in_progress";
	}
};

// -- Lifecycle phase --

export type LifecyclePhase =
	| "idle"
	| "creating"
	| "create_failed"
	| "polling"
	| "cancelled"
	| "complete"
	| "error"
	| "submitting"
	| "send_failed";

// -- Error extraction --

export const extractErrorMessage = (error: unknown, fallback: string): string => {
	if (error instanceof Error) {
		return error.message;
	}
	if (typeof error === "string") {
		return error;
	}
	return fallback;
};

// -- Stale session detection --

const HTTP_NOT_FOUND = 404;

const STALE_SESSION_PATTERN = /conversation.*not found|not found/iu;

const isErrorWithStatusCode = (error: unknown, statusCode: number): boolean =>
	typeof error === "object" && error != null && "statusCode" in error && error.statusCode === statusCode;

export const isStaleConversationError = (error: unknown): boolean => {
	if (isErrorWithStatusCode(error, HTTP_NOT_FOUND)) {
		return true;
	}
	if (!(error instanceof Error)) {
		return false;
	}
	return STALE_SESSION_PATTERN.test(error.message);
};
